import { Alert, AlertIcon, Badge, Box, Button, Flex, Skeleton, Stack, Text, type BoxProps } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Clock3, Pause, Play, Square } from 'lucide-react'
import type { ClockStatus } from '../../../api/time_clock/requests'
import { usePunch } from './hooks'
import { shiftDate } from './time'

export const Panel = (props: BoxProps) => (
  <Box bg="white" border="1px solid #ebe9f4" borderRadius="20px" p={{ base: 4, md: 6 }} minW={0} {...props} />
)
export function StateBadge({ state }: { state: string }) {
  const { t } = useTranslation()
  const scheme = ['working', 'approved', 'complete', 'inside'].includes(state)
    ? 'green'
    : ['paused', 'pending', 'missing', 'pause_short', 'auto_closed', 'changed', 'outside', 'unavailable'].includes(
          state,
        )
      ? 'orange'
      : ['confirmed', 'in_progress', 'correction_pending'].includes(state)
        ? 'purple'
        : 'gray'
  return (
    <Badge colorScheme={scheme} textTransform="none" borderRadius="full" px={3} py={1} fontWeight="500">
      {t(`timeClock.${state}`)}
    </Badge>
  )
}
export function QueryState({ loading, error, retry }: { loading: boolean; error: boolean; retry: () => unknown }) {
  const { t } = useTranslation()
  if (error)
    return (
      <Alert status="error" borderRadius="12px">
        <AlertIcon />
        <Text flex={1}>{t('timeClock.loadError')}</Text>
        <Button variant="white" size="sm" onClick={() => retry()}>
          {t('timeClock.retry')}
        </Button>
      </Alert>
    )
  if (loading)
    return (
      <Stack aria-label={t('timeClock.loading')}>
        <Skeleton height="120px" borderRadius="16px" />
        <Skeleton height="200px" borderRadius="16px" />
      </Stack>
    )
  return null
}
export function ClockActions({ status, compact = false }: { status: ClockStatus; compact?: boolean }) {
  const { t } = useTranslation()
  const punch = usePunch(status)
  return (
    <Flex gap={2} flexWrap="wrap">
      {status.allowed.map((kind) => {
        const ActionIcon = kind === 'out' ? Square : kind === 'pause_start' ? Pause : kind === 'in' ? Clock3 : Play
        return (
          <Button
            key={kind}
            size={compact ? 'sm' : 'lg'}
            flex={compact ? undefined : 1}
            minW={compact ? undefined : '135px'}
            colorScheme={kind === 'out' ? 'red' : kind === 'pause_start' ? 'orange' : 'purple'}
            variant={kind === 'out' || kind === 'pause_start' ? 'outline' : 'solid'}
            leftIcon={<ActionIcon size={compact ? 14 : 18} />}
            isLoading={punch.isPending && punch.variables === kind}
            isDisabled={punch.busy}
            onClick={() => punch.mutate(kind)}
          >
            {t(`timeClock.${kind}`)}
          </Button>
        )
      })}
    </Flex>
  )
}
export function WeekPicker({ start, onChange }: { start: string; onChange: (start: string) => void }) {
  const { t, i18n } = useTranslation()
  const label = (date: string) =>
    new Date(`${date}T12:00:00`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })
  return (
    <Flex align="center" gap={2} flexWrap="wrap">
      <Button
        variant="white"
        size="sm"
        aria-label={t('timeClock.previous')}
        onClick={() => onChange(shiftDate(start, -7))}
      >
        <ChevronLeft size={16} />
      </Button>
      <Text fontWeight="600" fontSize="sm">
        {label(start)} – {label(shiftDate(start, 6))}
      </Text>
      <Button variant="white" size="sm" aria-label={t('timeClock.next')} onClick={() => onChange(shiftDate(start, 7))}>
        <ChevronRight size={16} />
      </Button>
    </Flex>
  )
}
